import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {Object} LocalizationRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} countryInput
 * @property {HTMLInputElement} languageInput
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} triggerFlag
 * @property {HTMLElement} triggerLabel
 * @property {HTMLElement[]} countryButton
 * @property {HTMLElement} countryList
 * @property {HTMLElement} languageList
 * @property {HTMLElement[]} languageButton
 * @property {HTMLElement} noResults
 * @property {HTMLElement[]} letterGroup
 * @property {HTMLElement} selectedFlag
 * @property {HTMLElement} selectedName
 * @property {HTMLElement} selectedCurrency
 *
 * @extends {DialogComponent}
 */
class LocalizationSelector extends DialogComponent {
  requiredRefs = ['dialog', 'countryInput', 'languageInput', 'searchInput'];

  /** @type {string} */
  #selectedCountry = '';

  /** @type {string} */
  #selectedLanguage = '';

  connectedCallback() {
    super.connectedCallback();

    this.#selectedCountry = this.refs.countryInput.value;
    this.#selectedLanguage = this.refs.languageInput.value;

    this.#injectFlags();
    this.#updateActiveStates();
    this.#filterLanguages();
    this.#updateSelectedSummary();
  }

  openDialog() {
    this.showDialog();
    this.refs.searchInput.value = '';
    this.#showAllCountries();
  }

  closeLocalization() {
    this.closeDialog();
  }

  /**
   * @param {{ code: string }} data
   */
  selectCountry(data) {
    this.#selectedCountry = data.code;
    this.refs.countryInput.value = data.code;

    this.#updateActiveStates();
    this.#filterLanguages();
    this.#updateSelectedSummary();
  }

  /**
   * @param {{ code: string }} data
   */
  selectLanguage(data) {
    this.#selectedLanguage = data.code;
    this.refs.languageInput.value = data.code;

    this.#updateActiveStates();
  }

  filterCountries() {
    const query = this.refs.searchInput.value.trim().toLowerCase();

    if (!query) {
      this.#showAllCountries();
      return;
    }

    let visibleCount = 0;

    for (const btn of this.refs.countryButton ?? []) {
      const name = btn.getAttribute('data-name') ?? '';
      const code = btn.getAttribute('data-code')?.toLowerCase() ?? '';
      const match = name.includes(query) || code.includes(query);
      const li = btn.closest('li');
      if (li) li.hidden = !match;
      if (match) visibleCount++;
    }

    for (const header of this.refs.letterGroup ?? []) {
      let allHidden = true;
      let sibling = header.nextElementSibling;
      while (sibling && !sibling.classList.contains('localization__group-header')) {
        if (!(/** @type {HTMLElement} */ (sibling).hidden)) {
          allHidden = false;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      header.hidden = allHidden;
    }

    const noMatch = visibleCount === 0;

    if (this.refs.countryList) {
      this.refs.countryList.hidden = noMatch;
    }

    if (this.refs.noResults) {
      this.refs.noResults.hidden = !noMatch;
    }
  }

  submitLocalization() {
    const form = this.querySelector('form');
    form?.submit();
  }

  #showAllCountries() {
    for (const btn of this.refs.countryButton ?? []) {
      const li = btn.closest('li');
      if (li) li.hidden = false;
    }

    for (const group of this.refs.letterGroup ?? []) {
      group.hidden = false;
    }

    if (this.refs.countryList) {
      this.refs.countryList.hidden = false;
    }

    if (this.refs.noResults) {
      this.refs.noResults.hidden = true;
    }
  }

  #updateSelectedSummary() {
    const countryButtons = /** @type {HTMLElement[]} */ (this.refs.countryButton ?? []);
    const activeBtn = countryButtons.find(
      /** @param {HTMLElement} btn */
      (btn) => btn.getAttribute('data-code') === this.#selectedCountry
    );

    if (!activeBtn) return;

    const name = activeBtn.querySelector('.localization__option-label')?.textContent ?? '';
    const currency = activeBtn.getAttribute('data-currency') ?? '';
    const iso = activeBtn.getAttribute('data-code') ?? '';

    if (this.refs.selectedName) this.refs.selectedName.textContent = name;
    if (this.refs.selectedCurrency) this.refs.selectedCurrency.textContent = currency;

    if (this.refs.selectedFlag && iso.length === 2) {
      this.refs.selectedFlag.textContent = String.fromCodePoint(
        .../** @type {[number, number]} */ ([...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
      );
    }
  }

  #injectFlags() {
    const flagElements = this.querySelectorAll('[data-country-iso]');

    for (const el of flagElements) {
      const iso = el.getAttribute('data-country-iso') ?? '';
      if (iso.length === 2) {
        el.textContent = String.fromCodePoint(
          .../** @type {[number, number]} */ ([...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
        );
      }
    }
  }

  #updateActiveStates() {
    for (const btn of this.refs.countryButton ?? []) {
      btn.classList.toggle(
        'localization__option--active',
        btn.getAttribute('data-code') === this.#selectedCountry
      );
    }

    for (const btn of this.refs.languageButton ?? []) {
      const isActive = btn.getAttribute('data-code') === this.#selectedLanguage;
      btn.classList.toggle('localization__lang-option--active', isActive);
      const li = btn.closest('li');
      if (li) li.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  #filterLanguages() {
    const countryButtons = /** @type {HTMLElement[]} */ (this.refs.countryButton ?? []);
    const activeCountryBtn = countryButtons.find(
      /** @param {HTMLElement} btn */
      (btn) => btn.getAttribute('data-code') === this.#selectedCountry
    );

    if (!activeCountryBtn) return;

    /** @type {string[]} */
    let availableLanguages;

    try {
      availableLanguages = JSON.parse(activeCountryBtn.getAttribute('data-languages') ?? '[]');
    } catch {
      availableLanguages = [];
    }

    let hasSelectedLanguage = false;

    for (const btn of this.refs.languageButton ?? []) {
      const code = btn.getAttribute('data-code') ?? '';
      const isAvailable = availableLanguages.includes(code);
      const li = btn.closest('li');
      if (li) li.hidden = !isAvailable;

      if (isAvailable && code === this.#selectedLanguage) {
        hasSelectedLanguage = true;
      }
    }

    if (!hasSelectedLanguage && availableLanguages.length > 0) {
      this.#selectedLanguage = availableLanguages[0];
      this.refs.languageInput.value = this.#selectedLanguage;
      this.#updateActiveStates();
    }
  }
}

if (!customElements.get('localization-selector')) {
  customElements.define('localization-selector', LocalizationSelector);
}
